/**
 * CardButton Component
 *
 * Extracted from Figma Crop Circle System
 * Node ID: 852-1819
 *
 * A versatile card-style button with support for:
 * - Left section: Avatars, icons, checkbox
 * - Text section: Primary and secondary text
 * - Right section: Icons, checkbox
 * - Three color variants: White, Orange, Blue
 * - Three states: Default, Hover, Active
 */

import React, { useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { CheckIcon, Icon, type IconName } from '../icons';
import { colors } from '../tokens/colors';
import { Colors } from '../../../themes/colors';
import { themedStyleSheet } from '../../../themes/themedStyleSheet';

export interface CardButtonProps {
  /**
   * Primary text label
   * @default "Text"
   */
  text?: string;

  /**
   * Secondary text label
   * @default "Text 2"
   */
  text2?: string;

  /**
   * Show primary text
   * @default true
   */
  showText?: boolean;

  /**
   * Show secondary text
   * @default false
   */
  showText2?: boolean;

  /**
   * Left icon name
   */
  leftIcon?: IconName;

  /**
   * Show left icon
   * @default true
   */
  showLeftIcon?: boolean;

  /**
   * Right icon name
   */
  rightIcon?: IconName;

  /**
   * Show right icon
   * @default true
   */
  showRightIcon?: boolean;

  /**
   * Show avatar in left section
   * @default false
   */
  showAvatar?: boolean;

  /**
   * Show multiple avatars
   * @default true
   */
  showAvatars?: boolean;

  /**
   * Show left checkbox
   * @default false
   */
  showLeftCheckbox?: boolean;

  /**
   * Left checkbox checked state
   * @default false
   */
  leftCheckboxChecked?: boolean;

  /**
   * Show border around button
   * @default true
   */
  bordered?: boolean;

  /**
   * Callback when left checkbox is toggled
   */
  onLeftCheckboxChange?: (checked: boolean) => void;

  /**
   * Show right checkbox
   * @default false
   */
  showRightCheckbox?: boolean;

  /**
   * Right checkbox checked state
   * @default false
   */
  rightCheckboxChecked?: boolean;

  /**
   * Callback when right checkbox is toggled
   */
  onRightCheckboxChange?: (checked: boolean) => void;

  /**
   * Show entire left group section
   * @default true
   */
  showLeftGroup?: boolean;

  /**
   * Color variant
   * @default "white"
   */
  color?: 'white' | 'orange' | 'blue';

  /**
   * Active state
   * @default false
   */
  active?: boolean;

  /**
   * Callback when button is pressed
   */
  onPress?: () => void;

  /**
   * Override the computed background color for this instance.
   * Use when Figma specifies an instance-level fill override.
   */
  backgroundColor?: string;

  /**
   * Disabled state — ignores presses and dims the whole button. Always pair
   * with a visible explanation nearby; a disabled control with no reason
   * given reads as a bug rather than a state.
   * @default false
   */
  disabled?: boolean;
}

export const CardButton: React.FC<CardButtonProps> = ({
  text = 'Text',
  text2 = 'Text 2',
  showText = true,
  showText2 = false,
  leftIcon = 'Activity',
  showLeftIcon = true,
  rightIcon = 'Chevron Right',
  showRightIcon = true,
  showAvatar = false,
  showAvatars = true,
  showLeftCheckbox = false,
  leftCheckboxChecked = false,
  bordered = true,
  onLeftCheckboxChange,
  showRightCheckbox = false,
  rightCheckboxChecked = false,
  onRightCheckboxChange,
  showLeftGroup = true,
  color = 'white',
  active = false,
  onPress,
  backgroundColor: backgroundColorOverride,
  disabled = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  // Determine current state for styling
  const currentState = disabled
    ? 'default'
    : active
    ? 'active'
    : isPressed
    ? 'active'
    : isHovered
    ? 'hover'
    : 'default';

  // Get background color using Crop Circle color tokens
  const getBackgroundColor = () => {
    switch (color) {
      case 'blue':
        switch (currentState) {
          case 'active':
            return colors.semantic.infoDark; // #1F5B99
          case 'hover':
            return colors.semantic.info; // #4A90E2
          case 'default':
          default:
            return colors.semantic.infoLight; // #D0E5F9
        }
      case 'orange':
        switch (currentState) {
          case 'active':
            return colors.orange[500]; // #FF5505 - brand core
          case 'hover':
            return colors.orange[400]; // #FF8433
          case 'default':
          default:
            return colors.orange[300]; // #FFB368
        }
      case 'white':
      default:
        switch (currentState) {
          case 'active':
            return Colors.role.surfaceInverseSoftActive; // #A1A8B5
          case 'hover':
            return Colors.role.surfaceInverseSoftHover; // #C8CDD5
          case 'default':
          default:
            return Colors.role.surfaceInverseSoft; // #ECEEF1
        }
    }
  };

  // Per Figma all variants use brand black in light mode. The blue/orange
  // fills are mode-constant so their text stays literal black; the soft gray
  // fills flip dark, so their text rides textStrong (black -> white).
  const getTextColor = () => {
    if (color === 'blue' || color === 'orange') {
      return colors.brand.black; // #020202
    }
    return Colors.role.textStrong;
  };

  const getIconColor = () => getTextColor();

  const backgroundColor = backgroundColorOverride ?? getBackgroundColor();
  const textColor = getTextColor();
  const iconColor = getIconColor();

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      onPressIn={() => !disabled && setIsPressed(true)}
      onPressOut={() => !disabled && setIsPressed(false)}
      onHoverIn={() => !disabled && setIsHovered(true)}
      onHoverOut={() => !disabled && setIsHovered(false)}
      disabled={disabled}
      accessibilityState={{ disabled }}
      style={[
        styles.container,
        { backgroundColor },
        color === 'white' && bordered && styles.containerWithBorder,
        disabled && styles.disabledContainer,
      ]}
    >
      {/* Left Section */}
      {showLeftGroup && (
        <View style={styles.leftSection}>
          {showLeftCheckbox && (
            <Pressable
              style={[
                styles.checkbox,
                leftCheckboxChecked && styles.checkboxChecked,
                leftCheckboxChecked && {
                  backgroundColor: Colors.role.surfaceInverse,
                },
              ]}
              onPress={(e) => {
                e.stopPropagation();
                if (disabled) return;
                onLeftCheckboxChange?.(!leftCheckboxChecked);
              }}
              hitSlop={8}
            >
              {leftCheckboxChecked ? (
                <CheckIcon size={12} color={Colors.role.textOnInverse} />
              ) : (
                <View
                  style={[styles.checkboxInner, { borderColor: iconColor }]}
                />
              )}
            </Pressable>
          )}
          {showAvatars && !showLeftCheckbox && (
            <View style={styles.avatars}>
              {/* Placeholder for avatars - would need actual avatar component */}
              <View style={[styles.avatar, { backgroundColor: iconColor }]} />
            </View>
          )}
          {showLeftIcon && leftIcon && (
            <Icon name={leftIcon} size={24} color={iconColor} />
          )}
        </View>
      )}

      {/* Text Section */}
      <View style={styles.textSection}>
        {showText && (
          <Text
            style={[styles.primaryText, { color: textColor }]}
            numberOfLines={1}
          >
            {text}
          </Text>
        )}
        {showText2 && (
          <Text
            style={[styles.secondaryText, { color: textColor, opacity: 0.7 }]}
            numberOfLines={1}
          >
            {text2}
          </Text>
        )}
      </View>

      {/* Right Section */}
      <View style={styles.rightSection}>
        {showRightCheckbox && (
          <Pressable
            style={[
              styles.checkbox,
              rightCheckboxChecked && styles.checkboxChecked,
              rightCheckboxChecked && {
                backgroundColor: Colors.role.surfaceInverse,
              },
            ]}
            onPress={(e) => {
              e.stopPropagation();
              if (disabled) return;
              onRightCheckboxChange?.(!rightCheckboxChecked);
            }}
            hitSlop={8}
          >
            {rightCheckboxChecked ? (
              <CheckIcon size={12} color={Colors.role.textOnInverse} />
            ) : (
              <View
                style={[styles.checkboxInner, { borderColor: iconColor }]}
              />
            )}
          </Pressable>
        )}
        {/* 16 per Crop Circle (Figma Card Button, chevron "Chevron Right 16");
            was 24, which oversized the trailing chevron on every card field. */}
        {showRightIcon && rightIcon && (
          <Icon name={rightIcon} size={16} color={iconColor} />
        )}
      </View>
    </Pressable>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    height: 50,
    borderRadius: 2,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    cursor: 'pointer',
  },
  containerWithBorder: {
    borderWidth: 1,
    borderColor: role.borderControl, // #D8D8D8
  },
  // Matches SegmentedControl's disabled-option treatment (opacity 0.5) so
  // disabled Crop Circle controls read consistently across the app.
  disabledContainer: {
    opacity: 0.5,
    cursor: 'auto',
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  textSection: {
    flex: 1,
    justifyContent: 'center',
    gap: 2,
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  primaryText: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    color: role.textStrong,
  },
  secondaryText: {
    fontFamily: 'Inter',
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: role.textStrong,
  },
  checkbox: {
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 2,
  },
  checkboxChecked: {},
  checkboxInner: {
    width: 16,
    height: 16,
    borderRadius: 2,
    borderWidth: 2,
  },
  avatars: {
    flexDirection: 'row',
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
}));

CardButton.displayName = 'CardButton';
